import {positionPopupCloseButton} from './popup-close-button';
import {Fancybox} from '@fancyapps/ui';
import {getTranslation} from '../services/translations';
import {getLiveUpdateLoaderMarkup} from '../services/site-vars';
import {Notifications} from './notifications';

const notifications = new Notifications();
const popups = new Map();
const saves = new WeakMap();
const sectionSelector = '[data-popup-refresh-section]';

function elementOf(value) {
    return value?.jquery ? value[0] : value;
}

export function popupInstance(value) {
    return elementOf(value)?.closest('.fancybox__container')?.Fancybox || null;
}

function owns(root, element) {
    const popup = element.closest('.order-item-more-info');
    return !popup || popup === root || !root.contains(popup);
}

function controls(root, owner, outsideForms) {
    return Array.from(root.querySelectorAll('input, select, textarea, [contenteditable="true"]'))
        .filter(input => owns(owner, input) && (!outsideForms || !input.form) &&
            !['hidden', 'submit', 'button', 'reset'].includes(input.type));
}

function controlValue(input) {
    return [input.name, input.type, input.value, input.checked,
        input.selectedOptions ? Array.from(input.selectedOptions, option => option.value) : null,
        input.isContentEditable ? input.textContent : null];
}

function values(root, owner = root, outsideForms = false) {
    return JSON.stringify(controls(root, owner, outsideForms).map(controlValue));
}

function sections(root) {
    const parts = Array.from(root.querySelectorAll(sectionSelector)).filter(section => owns(root, section.parentElement));
    // Older renderers and simple dialogs are a single refreshable region.
    return parts.length ? parts : [root];
}

export function rememberPopupContent(value) {
    const root = elementOf(value);
    const state = popups.get(root);
    if (!state) {return;}
    state.baselines = new Map();
    state.forms = new WeakMap();
    for (const section of sections(root)) {rememberSection(root, section, state);}
}

function rememberSection(root, section, state) {
    state.baselines.set(section, values(section, root, true));
    for (const form of section.querySelectorAll('form')) {state.forms.set(form, values(form, root));}
}

export function registerPopupReload(value, load, loaded = null) {
    const root = elementOf(value);
    if (!root) {return;}
    const state = popups.get(root);
    if (state) {
        state.load = load;
        state.loaded = loaded;
        return;
    }
    popups.set(root, {load, loaded, version: 0, applied: 0, request: null, baselines: new Map(), forms: new WeakMap(), blockedBy: null, sync: 'ready'});
    rememberPopupContent(root);
}

export function popupNeedsRefresh(value) {
    const state = popups.get(elementOf(value));
    return !!state && state.version > state.applied;
}

export function capturePopupSave(value) {
    const element = elementOf(value);
    if (!element) {return null;}
    const instance = popupInstance(element);
    const containers = Array.from(document.querySelectorAll('.fancybox__container'));
    const index = containers.indexOf(instance?.$container);
    const roots = Array.from(popups.keys()).filter(root => {
        if (popupInstance(root)?.getSlide().$content !== root) {return false;}
        const containerIndex = containers.indexOf(root.closest('.fancybox__container'));
        return containerIndex >= 0 && containerIndex <= index;
    });
    const form = element.closest('form');
    const owner = roots.find(root => root.contains(form));
    const section = element.closest(sectionSelector) || roots.find(root => root.contains(element));
    const sectionOwner = roots.find(root => root.contains(section));
    const outsideIndex = section ? controls(section, sectionOwner || section, true).indexOf(element) : -1;
    const context = {instance, roots, owner, form, section, outsideIndex,
        submitted: form ? values(form, owner) : null,
        outside: outsideIndex >= 0 ? controlValue(element) : null};
    saves.set(element, context);
    return context;
}

// Called only by successful mutation handlers, never by a generic AJAX observer.
export function popupSaved(value, result = {}, captured = null) {
    const element = elementOf(value);
    const context = captured || saves.get(element) || capturePopupSave(element);
    if (!context) {return null;}
    if (saves.get(element) === context) {saves.delete(element);}
    for (const root of context.roots) {
        const state = popups.get(root);
        if (!state) {continue;}
        state.version++;
        // A later, unrelated save cannot prove a failed cloud projection recovered.
        if (result.sync_pending) {state.sync = 'pending';}
        // An open editor keeps its own live form; only its ancestors refresh on reveal.
        state.blockedBy = root.closest('.fancybox__container') === context.instance?.$container ? context.instance : null;
        if (context.form && root === context.owner) {state.forms.set(context.form, context.submitted);}
        if (context.outsideIndex >= 0 && state.baselines.has(context.section)) {
            const baseline = JSON.parse(state.baselines.get(context.section));
            baseline[context.outsideIndex] = context.outside;
            state.baselines.set(context.section, JSON.stringify(baseline));
        }
    }
    schedulePopupRefresh();
    return context.instance;
}

function notice(root, key) {
    let message = root.querySelector(':scope > [data-popup-refresh-notice]');
    if (!key) {
        message?.remove();
        return;
    }
    if (!message) {
        message = document.createElement('div');
        message.dataset.popupRefreshNotice = '';
        message.className = 'popup-refresh-notice';
        message.setAttribute('role', 'status');
        const text = document.createElement('span');
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = getTranslation('popup_refresh_retry');
        button.addEventListener('click', () => void refreshPopup(root));
        message.append(text, button);
        const header = root.querySelector(':scope > .ng-popup__header');
        if (header) {header.after(message);}
        else {root.prepend(message);}
    }
    message.firstElementChild.textContent = getTranslation(key);
}

function replaceSection(current, fresh) {
    const focused = current.ownerDocument.activeElement;
    const focusIndex = Array.from(current.querySelectorAll('input, select, textarea')).indexOf(focused);
    const selection = focusIndex >= 0 ? [focused.selectionStart, focused.selectionEnd] : null;
    const expanded = Array.from(current.querySelectorAll('[data-toggle-subscription].active, [data-toggle-subscription][aria-expanded="true"]'))
        .map(toggle => toggle.dataset.subscriptionId);
    const scroll = [current, ...current.querySelectorAll('*')]
        .filter(node => node.scrollTop || node.scrollLeft)
        .map(node => ({index: node === current ? -1 : Array.from(current.querySelectorAll('*')).indexOf(node), top: node.scrollTop, left: node.scrollLeft}));
    if (window.__fileUploadInstance && current.querySelector('[data-dropzone], .dropzone')) {
        window.__fileUploadInstance.destroyDropZones($(current));
    }
    for (const element of current.querySelectorAll('[data-tippy-content]')) {
        element._tippy?.destroy();
    }
    // Keep Fancybox's native control when a clean customer/ticket region reloads.
    const popup = current.closest('.order-item-more-info.customer, .order-item-more-info.support-ticket');
    const closeButton = popup?.querySelector(':scope > .carousel__button.is-close, :scope > .ng-popup__header > .carousel__button.is-close');
    const preserveClose = closeButton && current.contains(closeButton);
    if (preserveClose) closeButton.remove();
    current.replaceChildren(...fresh.childNodes);
    if (preserveClose) {
        popup.append(closeButton);
        positionPopupCloseButton(popup);
    }
    for (const id of expanded) {
        const toggle = Array.from(current.querySelectorAll('[data-toggle-subscription]')).find(node => node.dataset.subscriptionId === id);
        if (toggle) {
            toggle.classList.add('active');
            for (const row of current.querySelectorAll('[data-subscription-id]')) {
                if (row.dataset.subscriptionId === id && row.matches('[data-toggled-order]')) {row.style.display = '';}
            }
        }
    }
    const nextFocus = current.querySelectorAll('input, select, textarea')[focusIndex];
    if (nextFocus) {
        nextFocus.focus({preventScroll: true});
        if (typeof selection[0] === 'number') {nextFocus.setSelectionRange(...selection);}
    }
    const descendants = current.querySelectorAll('*');
    for (const position of scroll) {
        const node = position.index === -1 ? current : descendants[position.index];
        if (node) {
            node.scrollTop = position.top;
            node.scrollLeft = position.left;
        }
    }
}

function visible(root) {
    const instance = popupInstance(root);
    return instance && instance === Fancybox.getInstance() && instance.getSlide().$content === root && instance.state === 'ready';
}

export async function refreshPopup(value) {
    const root = elementOf(value);
    const state = popups.get(root);
    if (!state || !root.isConnected || !visible(root)) {return;}
    if (state.request) {return state.request;}
    const version = state.version;
    notice(root, null);
    const loading = notifications.loading(getLiveUpdateLoaderMarkup(), getTranslation('updating_data'));
    state.request = (async () => {
        try {
            const html = await state.load();
            if (!root.isConnected || !visible(root) || state.version !== version) {return;}
            const fresh = document.createElement('div');
            fresh.innerHTML = html;
            let retained = 0;
            const currentSections = sections(root);
            const freshKeys = sections(fresh).map(section => section.getAttribute('data-popup-refresh-section'));
            const currentKeys = currentSections.map(section => section.getAttribute('data-popup-refresh-section'));
            const hasDraft = section => values(section, root, true) !== state.baselines.get(section) ||
                Array.from(section.querySelectorAll('form')).some(form => owns(root, form) && values(form, root) !== state.forms.get(form)) ||
                Array.from(section.querySelectorAll('.processing, .dz-processing')).some(node => owns(root, node));
            const replaced = [];
            if (JSON.stringify(currentKeys) !== JSON.stringify(freshKeys)) {
                // A rolling renderer release can add/remove section boundaries.
                // Replace the whole popup only when every existing region is clean.
                if (currentSections.some(hasDraft)) {
                    retained++;
                } else {
                    replaceSection(root, fresh);
                    replaced.push(...sections(root));
                }
            } else {
                for (const section of currentSections) {
                    const key = section.getAttribute('data-popup-refresh-section');
                    const replacement = key ? sections(fresh).find(node => node.getAttribute('data-popup-refresh-section') === key) : fresh;
                    if (!replacement || hasDraft(section)) {
                        retained++;
                        continue;
                    }
                    replaceSection(section, replacement);
                    replaced.push(section);
                }
            }
            state.loaded?.();
            for (const section of replaced) {rememberSection(root, section, state);}
            if (!retained && state.sync === 'ready') {state.applied = version;}
            let message = state.sync === 'pending' ? 'popup_refresh_pending' : null;
            if (retained) { message = 'popup_refresh_draft'; }
            notice(root, message);
        } catch (error) {
            // The mutation already succeeded. Only offer to retry this read.
            if (root.isConnected && state.version === version) {notice(root, 'popup_refresh_failed');}
        } finally {
            notifications.remove(loading);
            state.request = null;
            if (state.version !== version) {schedulePopupRefresh();}
        }
    })();
    return state.request;
}

// Something outside a popup's own forms changed what it shows, e.g. a reply sent
// from the customer popup's email reader: reload its sections now.
export function popupContentChanged(value) {
    const element = elementOf(value);
    for (const [root, state] of popups) {
        if (!root.contains(element)) {continue;}
        state.version++;
        state.blockedBy = null;
    }
    schedulePopupRefresh();
}

export function schedulePopupRefresh() {
    // Fancybox's destroy event fires before inline placeholders are restored.
    window.setTimeout(() => {
        for (const [root, state] of popups) {
            if (!root.isConnected) {
                popups.delete(root);
                continue;
            }
            if (state.blockedBy?.state !== 'destroy' && state.blockedBy) {continue;}
            state.blockedBy = null;
            if (state.version > state.applied && visible(root)) {
                if (state.sync === 'pending') {notice(root, 'popup_refresh_pending');}
                else {void refreshPopup(root);}
            }
        }
    }, 0);
}
